import ConfirmModal from 'components/UI/ConfirmPopup.js';
import { usePerms } from 'contexts/PermsContext';
import { selectHistoryDataLoading } from 'features/Integrations/state/selectors/integrationSelectors';
import { useCallback, useMemo, useState } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from 'store/slices/Widget/widgetSlice.js';
import { USGS_PROVISIONAL_STATEMENT } from '../../assets/USGSProvisionalStatment';
import { usgsGetAllValuesForTimeSeriesId } from '../../utils/usgsGeneralUtils';
import {
  qualifierHandler,
  roundToHundredths,
  USGSMeasureNormalizer,
} from '../DeviceList/utils/usgsDataSheetUtils';
import { setDataSheetURLParams } from '../DeviceList/utils/urlUtils';
import { buildRow, formatTimestamp } from './DatasheetBuilderUtils';

const useCanEditUsgsDevice = (device) => {
  const perms = usePerms();
  return perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    [
      `Create-Edit${(device?.system_type || '').replaceAll(' ', '')}${(device?.device_type || '')
        .replaceAll(' ', '')
        .replaceAll('/', '-')}Connections`,
    ],
  );
};

const formatQualifier = (qualifier, displayValue) => {
  if (!Array.isArray(qualifier) || qualifier.length === 0) {
    return { display: 'N/A', overridesValue: false };
  }
  let qualifierValue = qualifierHandler(qualifier);
  if (qualifierValue === false && displayValue == null) {
    qualifierValue = <strong>Data Unavailable</strong>;
  }
  return {
    display: qualifierValue || qualifier.join(', '),
    overridesValue: Boolean(qualifierValue),
  };
};

const buildMeasurementValue = ({ renderedValue, showUnit, displayUnit, altValue, altUnit }) => (
  <span>
    <strong>{renderedValue ?? 'N/A'}</strong>
    {showUnit && displayUnit ? <span> {displayUnit}</span> : null}
    {altValue != null && altUnit ? (
      <span>
        {' '}
        / {altValue} {showUnit ? altUnit : ''}
      </span>
    ) : null}
  </span>
);

export const buildUsgsSections = (device, timeSeriesList, pageIndex) =>
  timeSeriesList
    .map((timeSeries) => {
      const timeSeriesID = String(timeSeries.id);
      const valuesForSeries = usgsGetAllValuesForTimeSeriesId(device, timeSeriesID);
      if (!Array.isArray(valuesForSeries) || valuesForSeries.length === 0) return null;

      const chosenRecord = valuesForSeries[pageIndex] || valuesForSeries[0];
      const normalized = USGSMeasureNormalizer(chosenRecord.value, timeSeries.unit_of_measure);
      const displayValue = roundToHundredths(chosenRecord.value);
      const displayUnit = normalized?.unit ?? timeSeries.unit_of_measure;
      const altUnit = normalized?.alt_unit ?? timeSeries.alt_unit_of_measure;
      const altValue = normalized?.alt_value ?? chosenRecord.alt_value;
      const { display: qualifierDisplay, overridesValue: qualifierOverridesValue } =
        formatQualifier(chosenRecord.qualifier, displayValue);
      const renderedValue = qualifierOverridesValue ? qualifierDisplay : displayValue;
      const showUnit = !qualifierOverridesValue;
      const timestampMs =
        typeof chosenRecord.ts === 'number' ? chosenRecord.ts : new Date(chosenRecord.ts).getTime();

      return {
        key: timeSeriesID,
        title: timeSeries.parameter_name,
        subtitle: `Monitoring Location: ${timeSeries.monitoring_location_id || device?.name || 'N/A'}`,
        meta: timeSeries.sublocation_identifier || null,
        rows: [
          buildRow(
            timeSeries.parameter_name,
            buildMeasurementValue({ renderedValue, showUnit, displayUnit, altValue, altUnit }),
            `${timeSeriesID}-value`,
          ),
          buildRow(
            'Station Name',
            device?.monitoring_location_name || 'N/A',
            `${timeSeriesID}-station`,
          ),
          buildRow('Qualifiers/Flags', qualifierDisplay, `${timeSeriesID}-qualifier`),
          buildRow(
            'GPS Location',
            device?.location ? `${device.location.lat}, ${device.location.lng}` : 'N/A',
            `${timeSeriesID}-gps`,
          ),
          buildRow(
            'Approval Status',
            chosenRecord?.approval_status ?? 'N/A',
            `${timeSeriesID}-approval`,
          ),
          buildRow(
            'Hydrologic Unit Code (HUC)',
            timeSeries.hydrologic_unit_code || device?.hydrologic_unit_code || 'N/A',
            `${timeSeriesID}-huc`,
          ),
          buildRow('Site Type', device?.site_type ?? 'N/A', `${timeSeriesID}-site-type`),
          buildRow(
            'Timestamp',
            Number.isFinite(timestampMs)
              ? new Date(timestampMs).toLocaleString(undefined, { timeZoneName: 'short' })
              : 'N/A',
            `${timeSeriesID}-timestamp`,
          ),
          buildRow('Timeseries', <small>{timeSeriesID}</small>, `${timeSeriesID}-timeseries`),
        ],
      };
    })
    .filter(Boolean);

export const useUsgsDatasheetBuilder = ({
  device,
  onClose,
  onSettingsOpen,
  footer,
  loadMore,
  hasMore = false,
  isFetching = false,
  error = null,
}) => {
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const errorMessage = error?.message ?? null;
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const canEditDevice = useCanEditUsgsDevice(device);

  const records = Array.isArray(device?.data) ? device.data : [];
  const timeSeriesList = useMemo(
    () => (Array.isArray(device?.time_series) ? device.time_series : []),
    [device?.time_series],
  );

  const handlePageChange = useCallback(
    (_page, record) => {
      const recordId = record?.did ?? record?.ts;
      if (!device?.id || recordId == null) return;
      setSearchParams((prev) => setDataSheetURLParams(prev, device.id, recordId), {
        replace: true,
      });
    },
    [device?.id, setSearchParams],
  );

  return useMemo(
    () => ({
      title: 'Device Data',
      records: timeSeriesList.length === 0 ? [] : records,
      loading: historyDataLoading || isFetching,
      loadingMessage: 'Loading device data...',
      emptyMessage:
        timeSeriesList.length === 0
          ? 'No USGS monitoring stations Data available.'
          : 'No USGS monitoring station Data available.',
      // The fetch failed: say so with the API's own words instead of "no data".
      errorMessage,
      onClose,
      onSettingsOpen,
      showSettings: canEditDevice,
      footer,
      onLoadMore: loadMore,
      hasMore,
      loadingMore: isFetching,
      getRecordId: (record) => record?.did ?? record?.ts,
      onPageChange: handlePageChange,
      subtitle: (record = {}) => (
        <>
          <strong className="d-block text-wrap mw-100 text-break">
            {device?.alias ||
              device?.monitoring_location_name ||
              device?.name ||
              'USGS Monitoring Station'}
          </strong>
          <small className="text-white d-block text-wrap mw-100 text-break">
            {record?.ts
              ? formatTimestamp(record.ts, { timeZoneName: 'short' })
              : 'Time series data for device'}
          </small>
        </>
      ),
      headerActions: (
        <IoMdInformationCircleOutline
          className="rounded-circle bg-primary p-1 cursor-pointer flex-shrink-0"
          style={{ height: 26, width: 26 }}
          onClick={() => setShowDisclaimerModal(true)}
        />
      ),
      notice: (
        <div className="px-2 pb-1" style={{ fontSize: 10 }}>
          <p className="mb-0 text-theme-muted">
            USGS Data may be{' '}
            <span
              className="text-decoration-underline cursor-pointer"
              onClick={() => setShowDisclaimerModal(true)}
            >
              provisional
            </span>
          </p>
        </div>
      ),
      sections: (_record, pageIndex) => buildUsgsSections(device, timeSeriesList, pageIndex),
      modal: (
        <ConfirmModal
          open={!provisionalAccepted || showDisclaimerModal}
          setOpen={setShowDisclaimerModal}
          options={['Continue']}
          callbacks={[
            () => {
              dispatch(setUsgsProvisonalAccepted(true));
              setShowDisclaimerModal(false);
            },
          ]}
          message={USGS_PROVISIONAL_STATEMENT}
          closeAction={() => {}}
          size="lg"
        />
      ),
      minBodyHeight: 0,
    }),
    [
      canEditDevice,
      device,
      dispatch,
      errorMessage,
      footer,
      handlePageChange,
      hasMore,
      historyDataLoading,
      isFetching,
      loadMore,
      onClose,
      onSettingsOpen,
      provisionalAccepted,
      records,
      showDisclaimerModal,
      timeSeriesList,
    ],
  );
};
